import type { ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Catalog.module.scss';
import type { SketchKind } from './sketchOf';

/**
 * Эскиз секции или страницы — серые полоски вместо скриншота: по нему
 * видно, что это за блок, ещё до того, как у него есть картинка.
 */
export function Sketch({ kind, className }: { kind: SketchKind; className?: string | undefined }) {
  return (
    <div className={cx(styles['sketch'], className)} aria-hidden="true">
      {SKETCHES[kind]}
    </div>
  );
}

/** Страница — эскизы первых секций стопкой. */
export function PageSketch({ kinds }: { kinds: SketchKind[] }) {
  return (
    <div className={cx(styles['sketch'], styles['sketch--page'])} aria-hidden="true">
      {(kinds.length === 0 ? (['text'] as SketchKind[]) : kinds.slice(0, 3)).map((kind, index) => (
        <div key={index} className={styles['sketch__part']}>
          {SKETCHES[kind]}
        </div>
      ))}
    </div>
  );
}

const bar = (width: string, strong = false, center = false) => (
  <span
    className={cx(styles['sk'], strong && styles['sk--strong'], center && styles['sk--center'])}
    style={{ width }}
  />
);

const row = (cells: number, strongLast = false, heights?: string[]) => (
  <span className={styles['sk-row']}>
    {Array.from({ length: cells }, (_, index) => (
      <span
        key={index}
        className={cx(styles['sk-cell'], strongLast && index === cells - 1 && styles['sk--strong'])}
        style={
          heights?.[index] === undefined
            ? undefined
            : { height: heights[index], alignSelf: 'flex-end' }
        }
      />
    ))}
  </span>
);

const SKETCHES: Record<SketchKind, ReactNode> = {
  hero: (
    <>
      {bar('70%', true, true)}
      {bar('45%', false, true)}
      <span className={styles['sk-row']} style={{ margin: '0 14%' }}>
        <span className={cx(styles['sk-cell'], styles['sk--strong'])} />
      </span>
    </>
  ),
  grid: (
    <>
      {bar('30%', true, true)}
      {row(3)}
      {row(3, true)}
    </>
  ),
  cards: (
    <>
      {bar('35%', true)}
      {row(4)}
    </>
  ),
  logos: (
    <>
      {bar('30%', true)}
      <span className={styles['sk-row']} style={{ alignItems: 'center' }}>
        {Array.from({ length: 5 }, (_, index) => (
          <span key={index} className={styles['sk-cell']} style={{ height: 14 }} />
        ))}
      </span>
    </>
  ),
  stats: (
    <>
      {bar('40%', true, true)}
      {bar('60%', false, true)}
      {row(4, false, ['60%', '85%', '50%', '95%'])}
    </>
  ),
  text: (
    <>
      {bar('55%', true)}
      {bar('100%')}
      {bar('92%')}
      {bar('76%')}
      {bar('84%')}
    </>
  ),
  image: (
    <>
      <span className={styles['sk-row']}>
        <span className={cx(styles['sk-cell'], styles['sk--strong'])} />
      </span>
      {bar('40%')}
    </>
  ),
  list: (
    <>
      {bar('35%', true)}
      {bar('100%')}
      {bar('100%')}
      {bar('100%')}
      {bar('100%')}
    </>
  ),
  form: (
    <>
      {bar('40%', true)}
      <span className={styles['sk-field']} />
      <span className={styles['sk-field']} />
      {bar('30%', true)}
    </>
  ),
};
